import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowLeft, ChevronLeft, ChevronRight, Check, Loader2, Minus, Plus } from "lucide-react";
import { AnnotationMode, getDocument, GlobalWorkerOptions } from "pdfjs-dist";
import type { PDFDocumentProxy, RenderTask } from "pdfjs-dist";
import "./TextbookReader.css";

GlobalWorkerOptions.workerSrc = new URL("pdfjs-dist/build/pdf.worker.min.mjs", import.meta.url).toString();

const ZOOMS = [75, 90, 100, 115, 130, 150];
const BASE_WIDTH = 760;

type Section = { label: string; page: number };
type Saved = { page: number; seen: number[] };

function storageKey(url: string) {
  return `acuity-reader:${url.replace(/[?#].*$/, "")}`;
}

function readSaved(url: string): Saved | null {
  try {
    const raw = window.localStorage.getItem(storageKey(url));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Saved;
    if (!Number.isFinite(parsed?.page) || !Array.isArray(parsed?.seen)) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeSaved(url: string, saved: Saved) {
  try {
    window.localStorage.setItem(storageKey(url), JSON.stringify(saved));
  } catch {
    /* private window or storage blocked: the reader still works */
  }
}

function bookTitle(name: string) {
  return name.replace(/\.pdf$/i, "").replace(/[_]+/g, " ").trim() || "Reading";
}

/** Top-level PDF bookmarks become the contents, each pointing at its first page. */
async function readSections(pdf: PDFDocumentProxy): Promise<Section[]> {
  try {
    const outline = await pdf.getOutline();
    if (!outline?.length) return [];
    const sections: Section[] = [];
    for (const entry of outline) {
      let dest = entry.dest;
      if (typeof dest === "string") dest = await pdf.getDestination(dest);
      if (!Array.isArray(dest) || !dest[0]) continue;
      const ref = dest[0];
      const index = typeof ref === "number" ? ref : await pdf.getPageIndex(ref);
      const label = String(entry.title || "").trim();
      if (label) sections.push({ label, page: index + 1 });
    }
    return sections.sort((a, b) => a.page - b.page);
  } catch {
    return [];
  }
}

/**
 * A teacher's PDF shown as a digital textbook: one page at a time, contents on
 * the side, a pages-read count, zoom, and no browser download/print bar. Opens
 * where the student left off.
 */
export default function TextbookReader({
  url,
  name,
  onClose,
  onFallback,
}: {
  url: string;
  name: string;
  onClose: () => void;
  /** Called when the PDF can't be drawn here, so the caller can show the plain viewer. */
  onFallback?: () => void;
}) {
  const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null);
  const [sections, setSections] = useState<Section[]>([]);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [seen, setSeen] = useState<Set<number>>(() => new Set([1]));
  const [zoom, setZoom] = useState(2);
  const [drawing, setDrawing] = useState(true);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const taskRef = useRef<RenderTask | null>(null);
  const total = pdf?.numPages ?? 0;

  useEffect(() => {
    let cancelled = false;
    let loaded: PDFDocumentProxy | null = null;
    setPdf(null);
    setError("");
    (async () => {
      loaded = await getDocument({ url }).promise;
      if (cancelled) return;
      const found = await readSections(loaded);
      if (cancelled) return;
      const saved = readSaved(url);
      const start = saved && saved.page >= 1 && saved.page <= loaded.numPages ? saved.page : 1;
      setSections(found);
      setSeen(new Set([...(saved?.seen ?? []).filter((n) => n >= 1 && n <= loaded!.numPages), start]));
      setPage(start);
      setPdf(loaded);
    })().catch(() => {
      if (cancelled) return;
      setError("This file couldn’t be opened as a book.");
      onFallback?.();
    });
    return () => {
      cancelled = true;
      taskRef.current?.cancel();
      void loaded?.destroy();
    };
    // onFallback is a one-shot escape hatch; reloading on its identity would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  useEffect(() => {
    if (!pdf || !canvasRef.current) return;
    let cancelled = false;
    setDrawing(true);
    (async () => {
      const pdfPage = await pdf.getPage(page);
      if (cancelled) return;
      const natural = pdfPage.getViewport({ scale: 1 });
      const stageWidth = Math.max(280, (stageRef.current?.clientWidth ?? BASE_WIDTH) - 48);
      const cssWidth = Math.min(stageWidth, (BASE_WIDTH * ZOOMS[zoom]) / 100);
      const scale = cssWidth / natural.width;
      const ratio = window.devicePixelRatio || 1;
      const viewport = pdfPage.getViewport({ scale: scale * ratio });
      const canvas = canvasRef.current;
      if (!canvas) return;
      const context = canvas.getContext("2d");
      if (!context) return;
      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);
      canvas.style.width = `${Math.floor(viewport.width / ratio)}px`;
      canvas.style.height = `${Math.floor(viewport.height / ratio)}px`;
      taskRef.current?.cancel();
      const task = pdfPage.render({ canvasContext: context, viewport, annotationMode: AnnotationMode.DISABLE });
      taskRef.current = task;
      await task.promise;
      if (!cancelled) setDrawing(false);
    })().catch(() => {
      /* a cancelled render (page turned again) is expected */
    });
    return () => {
      cancelled = true;
    };
  }, [pdf, page, zoom]);

  useEffect(() => {
    if (!pdf) return;
    writeSaved(url, { page, seen: [...seen] });
  }, [pdf, url, page, seen]);

  const goTo = useCallback(
    (next: number) => {
      if (!total) return;
      const to = Math.max(1, Math.min(total, next));
      setPage(to);
      setSeen((prev) => (prev.has(to) ? prev : new Set(prev).add(to)));
      stageRef.current?.scrollTo({ top: 0 });
    },
    [total],
  );

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
      if (event.key === "ArrowRight") goTo(page + 1);
      if (event.key === "ArrowLeft") goTo(page - 1);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [goTo, onClose, page]);

  const contents = useMemo<Section[]>(() => {
    if (sections.length) return sections;
    return Array.from({ length: total }, (_, i) => ({ label: `Page ${i + 1}`, page: i + 1 }));
  }, [sections, total]);

  const rows = contents.map((section, i) => {
    const end = i + 1 < contents.length ? contents[i + 1].page - 1 : total;
    let done = true;
    for (let n = section.page; n <= Math.max(section.page, end); n += 1) if (!seen.has(n)) done = false;
    return { ...section, end: Math.max(section.page, end), done, here: page >= section.page && page <= Math.max(section.page, end) };
  });

  const read = seen.size;
  const multi = total > 1;
  const title = bookTitle(name);

  // Rendered on <body> so it fills the screen even when opened from inside a
  // dialog (course preview), whose transform would otherwise trap it.
  return createPortal(
    <div className="al-reader" role="dialog" aria-modal="true" aria-label={title}>
      <header className="al-reader-head">
        <button type="button" className="al-reader-back" onClick={onClose}>
          <ArrowLeft size={16} aria-hidden />
          Back
        </button>
        <span className="al-reader-title">{title}</span>
        {total ? (
          <span className="al-reader-count" aria-live="polite">
            Page {page} of {total}
          </span>
        ) : null}
        <span className="al-reader-zoom" role="group" aria-label="Zoom">
          <button type="button" onClick={() => setZoom((z) => Math.max(0, z - 1))} disabled={zoom === 0} aria-label="Smaller">
            <Minus size={15} aria-hidden />
          </button>
          <span>{ZOOMS[zoom]}%</span>
          <button
            type="button"
            onClick={() => setZoom((z) => Math.min(ZOOMS.length - 1, z + 1))}
            disabled={zoom === ZOOMS.length - 1}
            aria-label="Bigger"
          >
            <Plus size={15} aria-hidden />
          </button>
        </span>
      </header>

      <div className="al-reader-body">
        {multi ? (
          <aside className="al-reader-toc" aria-label="Contents">
            <p className="al-reader-toc-label">Contents</p>
            <div className="al-reader-toc-list">
              {rows.map((row) => (
                <button
                  key={`${row.page}-${row.label}`}
                  type="button"
                  className={`al-reader-toc-row${row.here ? " is-here" : ""}`}
                  aria-current={row.here ? "page" : undefined}
                  onClick={() => goTo(row.page)}
                >
                  <span className={`al-reader-dot${row.done ? " is-done" : ""}`} aria-hidden>
                    {row.done ? <Check size={12} strokeWidth={3} /> : null}
                  </span>
                  <span className="al-reader-toc-name">{row.label}</span>
                  {sections.length ? <span className="al-reader-toc-page">p. {row.page}</span> : null}
                </button>
              ))}
            </div>
            <div className="al-reader-progress">
              <strong>
                {read} of {total} pages read
              </strong>
              <span className="al-reader-bar">
                <span style={{ width: `${Math.round((read / Math.max(1, total)) * 100)}%` }} />
              </span>
              <span className="al-reader-progress-note">Opens where you left off next time.</span>
            </div>
          </aside>
        ) : null}

        <div className="al-reader-stage" ref={stageRef}>
          {error ? <p className="al-reader-status">{error}</p> : null}
          {!error && !pdf ? (
            <p className="al-reader-status">
              <Loader2 size={18} className="al-reader-spin" aria-hidden /> Opening…
            </p>
          ) : null}
          <div className={`al-reader-page${pdf ? "" : " is-hidden"}${drawing ? " is-drawing" : ""}`} key={page}>
            <canvas ref={canvasRef} aria-label={`${title}, page ${page}`} role="img" />
          </div>
          {pdf && multi ? (
            <nav className="al-reader-nav" aria-label="Pages">
              <button type="button" className="al-reader-prev" onClick={() => goTo(page - 1)} disabled={page === 1}>
                <ChevronLeft size={16} aria-hidden />
                Previous page
              </button>
              <span className="al-reader-nav-count">
                {read} of {total} read
              </span>
              <button type="button" className="al-reader-next" onClick={() => goTo(page + 1)} disabled={page === total}>
                Next page
                <ChevronRight size={16} aria-hidden />
              </button>
            </nav>
          ) : null}
        </div>
      </div>
    </div>,
    document.body,
  );
}
